import { assoc } from 'ramda'

// Helper function to normalize the structure of IO Switches
const getIoSwitchesWithPanelNumber = (ioSwitches) =>
  (ioSwitches || []).reduce((prev, x) => {
    // Only consider switches with a single panel option (for simple diffs)
    if (x.switches.length === 1) {
      return assoc(x.id, x.switches[0].panelNumber, prev)
    }
    return prev
  }, {})

/**
 * @function getSwitchDiffOnUpdate
 * Calculates the IO switches that are added or removed when a feature is updated
 * to its latest snapshot.
 * @param {Object} previousItem - The feature in the current state (with old ioSwitches).
 * @returns {{added: Object, removed: Object}} Maps of added/removed switches (id: panelNumber).
 */
export const getSwitchDiffOnUpdate = (previousItem) => {
  const latestSnapshot = previousItem.latestSnapshot?.object?.feature

  if (!latestSnapshot) return { added: {}, removed: {} }

  const ioSwitchesFromPrevious = getIoSwitchesWithPanelNumber(
    previousItem.ioSwitches
  )
  const ioSwitchesFromSnapshot = getIoSwitchesWithPanelNumber(
    latestSnapshot.ioSwitches
  )

  const previousKeys = new Set(Object.keys(ioSwitchesFromPrevious))
  const snapshotKeys = new Set(Object.keys(ioSwitchesFromSnapshot))

  const removedSwitches = {}
  previousKeys.forEach((key) => {
    if (!snapshotKeys.has(key)) {
      removedSwitches[key] = ioSwitchesFromPrevious[key]
    }
  })

  const addedSwitches = {}
  snapshotKeys.forEach((key) => {
    if (!previousKeys.has(key)) {
      addedSwitches[key] = ioSwitchesFromSnapshot[key]
    }
  })

  return { addedSwitches, removedSwitches }
}

/**
 * @function buildSolenoidDetails
 * Builds the list of optional solenoids with feature details.
 * This is typically used for alerts when the available slot limit is exceeded.
 * @param {Array<Object>} markedNewAddition - Features marked for addition.
 * @param {Array<string>} newSolenoids - IDs of solenoids that are new.
 * @returns {Array<Object>} Details of the solenoids that exceed the limit.
 */
export const buildSolenoidDetails = (markedNewAddition, newSolenoids) =>
  markedNewAddition.reduce((prev, curr) => {
    if (curr.solenoidIds) {
      return [
        ...prev,
        ...curr.solenoidIds
          .filter((x) => newSolenoids.includes(x))
          .map((y, idx) => ({
            solenoidId: y,
            idx: idx + 1,
            moduleName: curr.moduleName,
            partNumber: curr.partNumber,
            featureId: curr.id,
            description: curr.description,
          })),
      ]
    }
    return prev
  }, [])
